import { BaseResource } from '/@/modules/core/app/resources/BaseResource';
import { Route } from '/@/modules/core/app/constants/api.constants';
import { AuthUser } from '/@/modules/auth/models/AuthUser';
import { SamlRedirect } from '/@/modules/auth/models/SamlRedirect';
import AuthLoginResponse from '@/modules/auth/models/AuthLoginResponse';
import { OtpLoginForm } from '@/modules/auth/models/OtpLoginForm';

export class AuthResource extends BaseResource {
  auth() {
    return this.getData(Route.AUTH_LOGIN, AuthUser);
  }

  async session() {
    return this.getData(Route.AUTH_SESSION, AuthUser);
  }

  /**
   * @param {SignUpForm} form
   * @return {Promise<AuthUser>}
   */
  async register(form) {
    const newUser = await this.postData(Route.AUTH_REGISTER, form);
    return AuthUser.mapModel(newUser);
  }

  async registerAdvisor(form) {
    const newUser = await this.postData(Route.AUTH_REGISTER_ADVISOR, form);

    return AuthUser.mapModel(newUser);
  }

  /**
   * @param {SignUpForm} form
   * @return {Promise<AuthUser>}
   */
  async registerSelfServePlan(form) {
    return this.postData(Route.AUTH_REGISTER_SELFSERVEPLAN, form);
  }
  /**
   * @param {SignUpForm} form
   * @return {Promise<AuthUser>}
   */
  async registerAftercare(form) {
    return this.postData(Route.AUTH_REGISTER_AFTERCARE, form);
  }

  /**
   * @param {SignUpForm} form
   * @return {Promise<AuthUser>}
   */
  async registerSettlement(form) {
    return this.postData(Route.AUTH_REGISTER_SETTLEMENT, form);
  }

  /**
   * @param {SignUpForm} form
   * @return {Promise<AuthUser>}
   */
  async registerVault(form) {
    const newUser = await this.postData(Route.AUTH_REGISTER_VAULT, form);
    return AuthUser.mapModel(newUser);
  }

  async registerPartnerSelfServeUser(form) {
    const newUser = await this.postData(Route.AUTH_REGISTER_PARTNER_SELF_SERVE_VAULT, form);
    return AuthUser.mapModel(newUser);
  }

  /**
   * @param form
   * @returns {Promise<AuthLoginResponse>}
   */
  async login(form) {
    return AuthLoginResponse.mapModel(await this.postData(Route.AUTH_LOGIN, form));
  }
  /**
   * @param {OtpLoginForm} form
   * @returns {Promise<AuthLoginResponse>}
   */
  async loginWithOtpToken(form) {
    return AuthLoginResponse.mapModel(await this.postData(Route.AUTH_LOGIN_OTP, form));
  }

  async loginWithGoogle(currentPath) {
    return this.getData(Route.AUTH_LOGIN_GOOGLE, AuthUser, { currentPath });
  }

  async loginWithSaml(form) {
    const response = await this.postData(Route.AUTH_LOGIN_SAML, form);

    return SamlRedirect.mapModel(response);
  }

  async loginAdvisorWithSaml(form) {
    const response = await this.postData(Route.AUTH_ADVISOR_LOGIN_SAML, form);

    return SamlRedirect.mapModel(response);
  }

  async loginWithGoogleSelfServePlan(currentPath, locale, invitationToken, billingPlanId) {
    return this.getData(Route.AUTH_LOGIN_GOOGLE_SELFSERVEPLAN, AuthUser, {
      currentPath,
      locale,
      invitationToken,
      billingPlanId
    });
  }

  async loginWithGoogleVault(currentPath, locale, vaultSignUpToken) {
    return this.getData(Route.AUTH_LOGIN_GOOGLE_VAULT, AuthUser, {
      currentPath,
      locale,
      vaultSignUpToken
    });
  }
  async loginWithGoogleAftercare(currentPath, locale, invitationToken) {
    return this.getData(Route.AUTH_LOGIN_GOOGLE_AFTERCARE, AuthUser, {
      currentPath,
      locale,
      invitationToken
    });
  }

  async loginWithFacebook() {
    return this.getData(Route.AUTH_LOGIN_FACEBOOK);
  }

  async logout() {
    return this.getData(Route.AUTH_LOGOUT);
  }

  sendResetPasswordLink(form) {
    return this.postData(Route.AUTH_SENDRESETPASSWORDLINK, form);
  }

  /**
   * @returns {Promise<string>}
   */
  sendVerifyEmailLink() {
    return this.getData(Route.AUTH_EMAIL_RESEND);
  }

  resetPassword(form) {
    return this.postData(Route.AUTH_RESETPASSWORD, form);
  }

  /**
   * @param {string} email
   * @param {string} token
   * @return {Promise<*>}
   */
  checkResetToken(email, token) {
    return this.getData(Route.AUTH_CHECKRESETTOKEN, AuthUser, { email, token });
  }

  /**
   * @param {BaseModel} form
   * @returns {Promise<string>}
   */
  changePassword(form) {
    return this.postData(Route.AUTH_CHANGEPASSWORD, form);
  }

  /**
   * @return {Promise<AuthUser>}
   * @param {number} userId
   * @param {number} estateId
   */
  async impersonateUser(userId, estateId) {
    const authUser = await this.postData(Route.AUTH_IMPERSONATEUSER, { userId, estateId });
    return AuthUser.mapModel(authUser);
  }

  /**
   * @return {Promise<AuthUser>}
   */
  async unimpersonateUser() {
    const authUser = await this.postData(Route.AUTH_UNIMPERSONATEUSER);
    return AuthUser.mapModel(authUser);
  }

  async completeTriage(estateTriage) {
    return this.postData(Route.ESTATES, estateTriage);
  }

  authenticateInvitation(form) {
    return this.postData(Route.AUTH_AUTHENTICATEINVITATION, form);
  }

  confirmInvitation(form) {
    return this.postData(Route.AUTH_CONFIRMINVITATION, form);
  }

  async getPermissionKeys() {
    return this.getData(Route.AUTH_PERMISSIONS);
  }
}

export default new AuthResource();
